import React from "react";

const QUILL_BACKGROUND_CLASSES = {
  "ql-bg-yellow": "#fff59d",
  "ql-bg-red": "#ffcdd2",
  "ql-bg-green": "#c8e6c9",
  "ql-bg-blue": "#bbdefb",
};

const MERGEABLE_WORD_SUFFIXES = new Set([
  "able",
  "al",
  "ance",
  "ant",
  "ary",
  "ate",
  "ation",
  "ed",
  "ence",
  "ent",
  "er",
  "ers",
  "ful",
  "ible",
  "ic",
  "ics",
  "ing",
  "ion",
  "ions",
  "ise",
  "ity",
  "ive",
  "ization",
  "ize",
  "less",
  "loading",
  "ly",
  "ment",
  "ments",
  "ness",
  "ng",
  "or",
  "ors",
  "ous",
  "ship",
  "tion",
  "tions",
  "ure",
  "ward",
  "wards",
  "work",
]);

const COMMON_SPLIT_WORDS = new Set([
  "actionable",
  "analytics",
  "application",
  "applications",
  "architecture",
  "based",
  "cloud",
  "component",
  "components",
  "data",
  "dashboard",
  "dashboards",
  "development",
  "education",
  "enable",
  "enabled",
  "engineering",
  "enterprise",
  "experience",
  "frontend",
  "healthcare",
  "hightraffic",
  "implemented",
  "insights",
  "interactive",
  "largescale",
  "learning",
  "loading",
  "management",
  "manufacturing",
  "medical",
  "memoization",
  "multiple",
  "optimized",
  "optimization",
  "performance",
  "platform",
  "platforms",
  "production",
  "professional",
  "projects",
  "realtime",
  "rendering",
  "responsive",
  "scalable",
  "software",
  "solutions",
  "standards",
  "strategies",
  "technologies",
  "technology",
  "visualization",
  "websocket",
]);

function hasRichText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fallbackNormalizeQuillHtml(value = "") {
  if (!hasRichText(value)) return "";

  return value.replace(
    /<([a-z0-9]+)([^>]*?)style=(["'])([^"']*background-color\s*:[^"']+)(\3)([^>]*)>/gi,
    (match, tagName, beforeStyle, quote, styleValue, _styleQuote, afterStyle) => {
      const existingClassMatch = `${beforeStyle}${afterStyle}`.match(/class=(["'])(.*?)\1/i);
      const existingClassName = existingClassMatch?.[2] || "";
      const mergedClassName = `${existingClassName} cv-inline-highlight highlight`
        .trim()
        .replace(/\s+/g, " ");

      if (existingClassMatch) {
        return `<${tagName}${beforeStyle.replace(existingClassMatch[0], `class="${mergedClassName}"`)}style=${quote}${styleValue}${quote}${afterStyle}>`;
      }

      return `<${tagName}${beforeStyle} class="${mergedClassName}" style=${quote}${styleValue}${quote}${afterStyle}>`;
    },
  );
}

function addHighlightClasses(root) {
  root.querySelectorAll("*").forEach((node) => {
    if (!(node instanceof Element)) return;

    const classNames = Array.from(node.classList);
    const hasQuillBackgroundClass = classNames.some((className) =>
      Object.prototype.hasOwnProperty.call(QUILL_BACKGROUND_CLASSES, className),
    );

    if (hasQuillBackgroundClass) {
      node.classList.add("cv-highlight");
    }

    const inlineBackground = node.style?.backgroundColor;
    if (inlineBackground) {
      node.classList.add("cv-highlight", "cv-inline-highlight", "highlight");
    }
  });
}

function shouldMergeWordFragments(left, right) {
  const safeLeft = String(left || "").toLowerCase();
  const safeRight = String(right || "").toLowerCase();

  if (!safeLeft || !safeRight) return false;
  if (!/^[a-z]+$/.test(safeLeft) || !/^[a-z]+$/.test(safeRight)) return false;

  if (COMMON_SPLIT_WORDS.has(`${safeLeft}${safeRight}`)) {
    return true;
  }

  return (
    (safeLeft.length >= 1 && safeRight.length >= 2 && MERGEABLE_WORD_SUFFIXES.has(safeRight)) ||
    (safeLeft.length >= 4 && safeRight.length <= 8 && MERGEABLE_WORD_SUFFIXES.has(safeRight))
  );
}

function repairSplitWordsInPlainText(value = "") {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  const tokens = normalized.split(" ").filter(Boolean);

  if (tokens.length <= 1) {
    return normalized;
  }

  const repairedTokens = [tokens[0]];

  for (let index = 1; index < tokens.length; index += 1) {
    const currentToken = tokens[index];
    const previousToken = repairedTokens[repairedTokens.length - 1];
    const previousLetters = (previousToken.match(/[A-Za-z]+$/) || [""])[0];
    const currentLetters = (currentToken.match(/^[A-Za-z]+/) || [""])[0];

    if (shouldMergeWordFragments(previousLetters, currentLetters)) {
      repairedTokens[repairedTokens.length - 1] = `${previousToken}${currentToken}`;
      continue;
    }

    repairedTokens.push(currentToken);
  }

  return repairedTokens.join(" ");
}

function repairTextNodes(root) {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((node) => {
    const repairedText = repairSplitWordsInPlainText(node.textContent || "");
    if (repairedText) {
      node.textContent = repairedText;
    }
  });
}

function shouldMergeWrappedBlocks(previousText = "", nextText = "") {
  const previous = String(previousText || "").trim();
  const next = String(nextText || "").trim();

  if (!previous || !next) return false;
  if (!/[A-Za-z-]$/.test(previous)) return false;
  if (!/^[a-z]/.test(next)) return false;

  const previousWord = (previous.match(/([A-Za-z-]+)$/) || ["", ""])[1].replace(/-$/, "");
  const nextWord = (next.match(/^([a-z]+)/) || ["", ""])[1];

  if (!previousWord || !nextWord) return false;
  if (previous.endsWith("-")) return true;

  return (
    previousWord.length >= 4 &&
    previousWord.length <= 8 &&
    nextWord.length >= 2 &&
    nextWord.length <= 8
  );
}

function mergeSoftWrappedBlocks(root) {
  let current = root.firstElementChild;

  while (current?.nextElementSibling) {
    const next = current.nextElementSibling;
    const currentText = current.textContent || "";
    const nextText = next.textContent || "";

    if (shouldMergeWrappedBlocks(currentText, nextText)) {
      const joiner = currentText.trim().endsWith("-") ? "" : "";
      current.innerHTML = `${current.innerHTML}${joiner}${next.innerHTML}`;
      next.remove();
      continue;
    }

    current = next;
  }
}

export function normalizeQuillHtml(value = "") {
  if (!hasRichText(value)) return "";

  if (typeof DOMParser === "undefined") {
    return fallbackNormalizeQuillHtml(value);
  }

  const parser = new DOMParser();
  const documentFragment = parser.parseFromString(
    `<div>${value}</div>`,
    "text/html",
  );
  const root = documentFragment.body.firstElementChild;

  if (!root) {
    return fallbackNormalizeQuillHtml(value);
  }

  addHighlightClasses(root);
  repairTextNodes(root);
  mergeSoftWrappedBlocks(root);
  return root.innerHTML;
}

export function htmlToRichListItems(value = "", limit = Number.MAX_SAFE_INTEGER) {
  if (!hasRichText(value)) return [];

  const normalizedHtml = normalizeQuillHtml(value);

  if (typeof DOMParser === "undefined") {
    const listMatches = Array.from(
      normalizedHtml.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi),
    )
      .map((match) => match[1].trim())
      .filter(Boolean);

    if (listMatches.length > 0) {
      return listMatches.slice(0, limit);
    }

    return [normalizedHtml].slice(0, limit);
  }

  const parser = new DOMParser();
  const documentFragment = parser.parseFromString(
    `<div>${normalizedHtml}</div>`,
    "text/html",
  );
  const root = documentFragment.body.firstElementChild;

  if (!root) return [];

  addHighlightClasses(root);

  const listItems = Array.from(root.querySelectorAll("li"))
    .map((item) => item.innerHTML.trim())
    .filter(Boolean);

  const nonListBlockItems = Array.from(root.children)
    .flatMap((child) => {
      const tagName = child.tagName?.toLowerCase?.() || "";

      if (tagName === "ul" || tagName === "ol") {
        return [];
      }

      const html = child.innerHTML.trim() || child.textContent?.trim() || "";
      return html ? [html] : [];
    })
    .filter(Boolean);

  if (listItems.length > 0) {
    return [...listItems, ...nonListBlockItems].slice(0, limit);
  }

  const blockItems = Array.from(root.children)
    .map((child) => child.innerHTML.trim() || child.textContent?.trim() || "")
    .filter(Boolean);

  if (blockItems.length > 0) {
    return blockItems.slice(0, limit);
  }

  const plain = root.innerHTML.trim();
  return plain ? [plain].slice(0, limit) : [];
}

export function getAchievementHtmlItems(data, limit = Number.MAX_SAFE_INTEGER) {
  return htmlToRichListItems(data?.achievements || "", limit);
}

export function getExperienceHighlightHtmlItems(
  experience,
  limit = Number.MAX_SAFE_INTEGER,
) {
  void limit;

  const items = htmlToRichListItems(
    experience?.description || "",
    Number.MAX_SAFE_INTEGER,
  );

  if (items.length > 0) {
    return items;
  }

  if (hasRichText(experience?.project)) {
    return [escapeHtml(experience.project.trim())];
  }

  return [];
}

export function ResumeRichText({
  html,
  as: Component = "div",
  className = "",
  style,
  dataField,
}) {
  const normalizedHtml = normalizeQuillHtml(html);
  const textContent = String(html || "").replace(/<[^>]*>/g, " ").trim();

  if (!textContent) return null;

  return (
    <Component
      className={["cv-container", "cv-rich-text", className].filter(Boolean).join(" ")}
      style={{
        display: "block",
        minWidth: 0,
        maxWidth: "100%",
        whiteSpace: "normal",
        wordBreak: "normal",
        overflowWrap: "break-word",
        hyphens: "none",
        ...style,
      }}
      data-field={dataField}
      dangerouslySetInnerHTML={{ __html: normalizedHtml }}
    />
  );
}

export function ResumeRichTextInline({
  html,
  className = "",
  style,
  dataField,
}) {
  const normalizedHtml = normalizeQuillHtml(html);
  const textContent = String(html || "").replace(/<[^>]*>/g, " ").trim();

  if (!textContent) return null;

  return (
    <span
      className={["cv-container", "cv-rich-text-inline", className].filter(Boolean).join(" ")}
      style={{
        display: "block",
        minWidth: 0,
        maxWidth: "100%",
        whiteSpace: "normal",
        wordBreak: "normal",
        overflowWrap: "break-word",
        hyphens: "none",
        ...style,
      }}
      data-field={dataField}
      dangerouslySetInnerHTML={{ __html: normalizedHtml }}
    />
  );
}

export const QUILL_HIGHLIGHT_SUPPORT_CSS = `
.cv-container .ql-bg-yellow,
.cv-container .highlight.ql-bg-yellow,
.cv-container .highlight-yellow {
  background-color: #fff59d !important;
}

.cv-container .ql-bg-red,
.cv-container .highlight.ql-bg-red,
.cv-container .highlight-red {
  background-color: #ffcdd2 !important;
}

.cv-container .ql-bg-green,
.cv-container .highlight.ql-bg-green,
.cv-container .highlight-green {
  background-color: #c8e6c9 !important;
}

.cv-container .ql-bg-blue,
.cv-container .highlight.ql-bg-blue,
.cv-container .highlight-blue {
  background-color: #bbdefb !important;
}

.cv-container .cv-highlight,
.cv-container .highlight,
.cv-container span[style*="background-color"],
.cv-container [style*="background-color"] {
  padding: 0 2px;
  border-radius: 2px;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
}

.cv-container .cv-rich-text,
.cv-container .cv-rich-text-inline {
  background: transparent;
}

.cv-container .cv-rich-text p,
.cv-container .cv-rich-text ul,
.cv-container .cv-rich-text ol {
  margin: 0;
}

@media print {
  .cv-container *,
  .cv-container *::before,
  .cv-container *::after {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;
